/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { IPersonaProps } from '@fluentui/react/lib/Persona';
import * as React from 'react';

import {
    blockMainActionVar,
    CreateTemplateInstanceWizardSteps,
    currentModeVar,
    currentStepVar,
    Mode,
    templateInstanceCategoryVar,
    templateInstanceDescriptionVar,
    templateInstanceIconBackgroundColorVar,
    templateInstanceIconNameVar,
    templateInstanceSpecificPeopleVar,
    templateInstanceStatusVar,
    templateInstanceTitleVar,
} from '../../../../../apollo/reactiveVariables/TemplateInstanceVariables';
import {
    currentScopeInCreationVar,
    modalDataVar,
    ModalType,
    TemplateScope,
} from '../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import { Constants } from '../../../../../common/Constants';

import {
    LogCategory,
    PanelRegion,
    PanelType,
    ScenarioName,
    StatusName,
    StepName,
    SubWorkLoad,
} from '../../../../../logger';
import { ApprovalPersonaProps } from '../../../../../models/ApprovalPersonaProps';
import { ApprovalTemplateCategory, VisibilityPrincipal } from '../../../../../service/gen/templates';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { BasicSettingsComponent, BasicSettingsComponentProps } from './BasicSettings.component';

const defaultIconName = 'AppIconDefault';
const defaultIconBackgroundColor = '#6264A7';

export const BasicSettingsContainer: React.FC = (): JSX.Element => {
    const host = getAppHost();
    const loc = host.getLocale();
    const logger = host.getLogger();
    const [nameError, setNameError] = React.useState<string>('');
    const [descriptionError, setDescriptionError] = React.useState<string>('');
    const [specificPeopleError, setSpecificPeopleError] = React.useState<string>('');

    const title = useReactiveVar(templateInstanceTitleVar);
    const iconName = useReactiveVar(templateInstanceIconNameVar);
    const iconBackgroundColor = useReactiveVar(templateInstanceIconBackgroundColorVar);

    const description = useReactiveVar(templateInstanceDescriptionVar);
    const category = useReactiveVar(templateInstanceCategoryVar);
    const specificPeople = useReactiveVar(templateInstanceSpecificPeopleVar);
    const status = useReactiveVar(templateInstanceStatusVar);

    const currentMode = useReactiveVar(currentModeVar);
    const currentStep = useReactiveVar(currentStepVar);

    const currentScope = useReactiveVar(currentScopeInCreationVar);

    let showSpecificPeople = false;
    let showScopeSettings = false;
    let showStatus = false;
    if (currentMode === Mode.Create && currentScope === TemplateScope.TenantSpecificPeople) {
        showSpecificPeople = true;
    } else if (currentMode === Mode.Edit) {
        showStatus = true;

        if (currentScope === TemplateScope.TenantAll || currentScope === TemplateScope.TenantSpecificPeople) {
            showScopeSettings = true;
            if (currentScope === TemplateScope.TenantSpecificPeople) {
                showSpecificPeople = true;
            }
        }
    }

    const onNameChange = (errorMessage: string, newValue: string): void => {
        templateInstanceTitleVar(newValue);

        if (!newValue) {
            setNameError(loc.strings.required);
        } else {
            setNameError(errorMessage);
        }
    };

    const onCategoryChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        templateInstanceCategoryVar(option.key as ApprovalTemplateCategory);
    };

    const onDescriptionChange = (errorMessage: string, newValue: string): void => {
        if (!errorMessage) {
            templateInstanceDescriptionVar(newValue);
        }

        setDescriptionError(errorMessage);
    };

    const onSpecificPeopleChange = (specificPeopleSelected: IPersonaProps[]): void => {
        if (specificPeopleSelected.length > Constants.MAX_NUMBER_OF_SPECIFIC_PEOPLE) {
            logger.logTraceWarning(LogCategory.TemplateInstanceWizard, 'Maximum number of specific people reached!');
            setSpecificPeopleError(loc.strings.maximum_approvers_error);
        } else {
            setSpecificPeopleError('');
        }

        templateInstanceSpecificPeopleVar(
            specificPeopleSelected.map((item: ApprovalPersonaProps): VisibilityPrincipal => {
                return {
                    PrincipalId: item.id,
                    DisplayName: item.text,
                    PrincipalType: item.personaType,
                };
            })
        );
    };

    const onScopeSettingsChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        if (specificPeopleError) setSpecificPeopleError('');
        currentScopeInCreationVar(option.key as TemplateScope);
    };

    const onIconClick = (): void => {
        logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.OpenIconColorPicker, StatusName.NA);
        logger.logUserBiAction(
            SubWorkLoad.AppInteraction,
            ScenarioName.CreateTemplateInstance,
            StepName.OpenIconColorPicker,
            'click',
            'nav',
            'templateIcon',
            'templateIconButton',
            PanelRegion.Modal,
            PanelType.IconColorPicker
        );

        modalDataVar({ isOpen: true, type: ModalType.ChangeTemplateInstanceIcon });
    };

    const onStatusChange = (event: React.MouseEvent<HTMLElement, MouseEvent>, checked?: boolean): void => {
        templateInstanceStatusVar(checked);
    };

    React.useEffect((): void => {
        if (!iconName) {
            templateInstanceIconNameVar(defaultIconName);
        }

        if (!iconBackgroundColor) {
            templateInstanceIconBackgroundColorVar(defaultIconBackgroundColor);
        }
    }, [iconName, iconBackgroundColor]);

    React.useEffect((): void => {
        if (currentStep === CreateTemplateInstanceWizardSteps.BasicSettings) {
            const isSpecificPeopleEmpty =
                currentScope === TemplateScope.TenantSpecificPeople && (!specificPeople || specificPeople.length === 0);

            if (!title || nameError || descriptionError || specificPeopleError || isSpecificPeopleEmpty) {
                if (isSpecificPeopleEmpty) {
                    setSpecificPeopleError(loc.strings.required);
                }

                blockMainActionVar(true);
            } else {
                blockMainActionVar(false);
            }
        }
    }, [title, nameError, descriptionError, specificPeopleError, currentStep, currentScope, specificPeople]);

    const componentProps: BasicSettingsComponentProps = {
        iconName,
        iconBackgroundColor,
        onNameChange,
        title,
        nameError,
        onDescriptionChange,
        descriptionError,
        description,
        specificPeople,
        specificPeopleError,
        onSpecificPeopleChange,
        showSpecificPeople,
        // Category is null means creating from scratch, then set to others
        category: category || ApprovalTemplateCategory.NotSpecified,
        onCategoryChange,
        scope: currentScope,
        showScopeSettings,
        onScopeSettingsChange,
        onIconClick,
        showStatus,
        status,
        onStatusChange,
    };

    return <BasicSettingsComponent {...componentProps} />;
};
